<template>
  <div
    ref="buttonRef"
    class="fixed z-[500] select-none touch-none fab-float"
    :style="positionStyle"
    @mousedown="onDragStart"
    @touchstart.passive="onDragStart"
  >

    <!-- Sparkle particles -->
    <span class="sparkle sparkle-1" />
    <span class="sparkle sparkle-2" />
    <span class="sparkle sparkle-3" />
    <span class="sparkle sparkle-4" />
    <span class="sparkle sparkle-5" />
    <span class="sparkle sparkle-6" />
    <span class="sparkle sparkle-7" />
    <span class="sparkle sparkle-8" />

    <!-- Ambient glow -->
    <span class="absolute inset-[-8px] rounded-full fab-glow" />

    <button
      class="relative h-14 pr-4 pl-5 rounded-full flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 bg-gradient-to-br from-[#7c3aed] via-[#6d28d9] to-[#4c1d95] shadow-lg shadow-purple-900/50"
      @click.stop="handleClick"
      aria-label="עוזר AI"
    >
      <!-- AI sparkle icon -->
      <svg class="relative z-10 w-6 h-6 text-white ai-sparkle" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L13.65 8.35L20 10L13.65 11.65L12 18L10.35 11.65L4 10L10.35 8.35L12 2Z" />
        <path d="M19 14L19.7 16.3L22 17L19.7 17.7L19 20L18.3 17.7L16 17L18.3 16.3L19 14Z" opacity="0.85" />
      </svg>
      <span class="relative z-10 text-white font-bold text-base tracking-wide leading-none whitespace-nowrap">עוזר AI</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {useAiAgentStore} from '@app/stores/ai-agent';
import {useDrawer, useSharedStore} from '@torim-app/shared-components';
import AiAgentDrawer from './AiAgentDrawer.vue';
import { trackEvent } from '@app/composables/posthog';

const store = useAiAgentStore();
const sharedStore = useSharedStore();
const {openDrawer} = useDrawer();

const STORAGE_KEY = 'ai-fab-position';
const DRAG_THRESHOLD = 6;

const buttonRef = ref<HTMLElement | null>(null);
const position = ref({x: 16, y: 0});
const isDragging = ref(false);
const dragStartPos = ref({x: 0, y: 0});
const dragStartButtonPos = ref({x: 0, y: 0});
const hasDragged = ref(false);

const bottomInset = computed(() => sharedStore.bottomPadding ?? 0);

const positionStyle = computed(() => ({
  left: `${position.value.x}px`,
  bottom: `${position.value.y + bottomInset.value + 16}px`,
}));

const loadPosition = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      position.value = {x: parsed.x ?? 16, y: parsed.y ?? 0};
    }
  } catch {
    // use defaults
  }
};

const savePosition = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(position.value));
};

const getEventPos = (e: MouseEvent | TouchEvent) => {
  if ('touches' in e) {
    return {x: e.touches[0].clientX, y: e.touches[0].clientY};
  }
  return {x: e.clientX, y: e.clientY};
};

const onDragStart = (e: MouseEvent | TouchEvent) => {
  const pos = getEventPos(e);
  dragStartPos.value = pos;
  dragStartButtonPos.value = {...position.value};
  isDragging.value = true;
  hasDragged.value = false;

  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
  document.addEventListener('touchmove', onDragMove, {passive: false});
  document.addEventListener('touchend', onDragEnd);
};

const onDragMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  const pos = getEventPos(e);
  const dx = pos.x - dragStartPos.value.x;
  const dy = pos.y - dragStartPos.value.y;

  if (!hasDragged.value && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
  hasDragged.value = true;

  if ('cancelable' in e && e.cancelable) e.preventDefault();

  const btnWidth = buttonRef.value?.offsetWidth ?? 56;
  const btnHeight = buttonRef.value?.offsetHeight ?? 56;
  const maxX = window.innerWidth - btnWidth;
  const maxY = window.innerHeight - btnHeight - bottomInset.value - 16;

  position.value = {
    x: Math.max(0, Math.min(maxX, dragStartButtonPos.value.x + dx)),
    y: Math.max(0, Math.min(maxY, dragStartButtonPos.value.y - dy)),
  };
};

const onDragEnd = () => {
  isDragging.value = false;
  if (hasDragged.value) savePosition();

  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  document.removeEventListener('touchmove', onDragMove);
  document.removeEventListener('touchend', onDragEnd);
};

const handleClick = () => {
  if (hasDragged.value) return;
  trackEvent('ai_floating_button_clicked');
  store.openDrawer();
  openDrawer(AiAgentDrawer, {}, {full: true, closable: true, noPadding: true, bgColor: '#0f0a1e'});
};

onMounted(loadPosition);
onUnmounted(() => {
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  document.removeEventListener('touchmove', onDragMove);
  document.removeEventListener('touchend', onDragEnd);
});
</script>

<style scoped>
/* --- Floating hover animation --- */
.fab-float {
  animation: fabFloat 3s ease-in-out infinite;
}

@keyframes fabFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* --- Ambient glow with color shift --- */
.fab-glow {
  background: radial-gradient(circle, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.15), transparent 70%);
  animation: fabGlow 3s ease-in-out infinite;
}

@keyframes fabGlow {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
    filter: hue-rotate(0deg);
  }
  50% {
    transform: scale(1.35);
    opacity: 0.1;
    filter: hue-rotate(25deg);
  }
}

/* --- AI sparkle icon pulse --- */
.ai-sparkle {
  animation: aiSparklePulse 2.4s ease-in-out infinite;
  transform-origin: center;
}

@keyframes aiSparklePulse {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(12deg) scale(1.08); }
}

/* --- Sparkle particles --- */
.sparkle {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: white;
  pointer-events: none;
  opacity: 0;
  top: 50%;
  left: 50%;
}

.sparkle-1 {
  animation: sparkleOut1 2.4s ease-out infinite;
  animation-delay: 0s;
}
.sparkle-2 {
  animation: sparkleOut2 2.8s ease-out infinite;
  animation-delay: 0.4s;
}
.sparkle-3 {
  animation: sparkleOut3 2.2s ease-out infinite;
  animation-delay: 0.8s;
}
.sparkle-4 {
  animation: sparkleOut4 3s ease-out infinite;
  animation-delay: 1.2s;
}
.sparkle-5 {
  animation: sparkleOut5 2.6s ease-out infinite;
  animation-delay: 0.2s;
}
.sparkle-6 {
  animation: sparkleOut6 2.5s ease-out infinite;
  animation-delay: 1.5s;
}
.sparkle-7 {
  animation: sparkleOut7 2.9s ease-out infinite;
  animation-delay: 0.7s;
}
.sparkle-8 {
  animation: sparkleOut8 2.3s ease-out infinite;
  animation-delay: 1.8s;
}

@keyframes sparkleOut1 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.9; transform: translate(-8px, -20px) scale(1); }
  100% { transform: translate(-18px, -40px) scale(0); opacity: 0; }
}
@keyframes sparkleOut2 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.8; transform: translate(18px, -15px) scale(1.1); }
  100% { transform: translate(35px, -30px) scale(0); opacity: 0; }
}
@keyframes sparkleOut3 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.85; transform: translate(22px, 5px) scale(0.9); }
  100% { transform: translate(40px, 8px) scale(0); opacity: 0; }
}
@keyframes sparkleOut4 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.7; transform: translate(-20px, 10px) scale(1); }
  100% { transform: translate(-38px, 18px) scale(0); opacity: 0; }
}
@keyframes sparkleOut5 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.9; transform: translate(5px, -22px) scale(0.8); }
  100% { transform: translate(10px, -42px) scale(0); opacity: 0; }
}
@keyframes sparkleOut6 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.75; transform: translate(-22px, -8px) scale(1.05); }
  100% { transform: translate(-42px, -14px) scale(0); opacity: 0; }
}
@keyframes sparkleOut7 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.8; transform: translate(12px, 18px) scale(0.85); }
  100% { transform: translate(22px, 36px) scale(0); opacity: 0; }
}
@keyframes sparkleOut8 {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  15% { opacity: 0.85; transform: translate(-5px, 22px) scale(1); }
  100% { transform: translate(-10px, 40px) scale(0); opacity: 0; }
}
</style>
